import React, { useEffect, useRef, useState } from "react";
import { PhoneCall, MoonStars, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { autoPrefixUS, isValidE164 } from "../../components/PhoneInput";
import { useSystemMode } from "../../hooks/useSystemMode";

const ESCALATION_CATEGORIES = [
  { key: "unassigned", label: "Unassigned trips", detail: "Nova auto-assigns the nearest cleared driver instead of ringing the desk." },
  { key: "chain_proposal", label: "Nova's route proposals", detail: "Nova approves its own multi-stop proposal with its suggested driver." },
  { key: "chain_alert", label: "Chain drift reassignments", detail: "Nova reassigns the at-risk leg to its suggested driver, or dismisses if it has no better option." },
];

/**
 * My Profile — self-service phone number, plus a read-through of the ONE
 * global System Mode (see Floor's mode pill — same hook, same source of
 * truth) and the desk-wide escalation categories admins may pre-approve.
 *
 * P0 (2026-10-16): Away Mode and "pre-approve" categories used to be a
 * PER-USER toggle here. That is exactly how the Floor could show Away OFF
 * for the person looking at it while the system still behaved like Away
 * was ON — some other account still had their personal toggle set. Mode
 * is now one switch for the whole desk (core/mode_policy.py), changed from
 * the Floor itself or below, never from an individual's profile.
 */
export default function MyProfilePanel() {
  const [me, setMe] = useState(null);
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const sysMode = useSystemMode();

  const [categories, setCategories] = useState([]);
  const [categoriesSaving, setCategoriesSaving] = useState(false);
  const categoriesInitRef = useRef(false);
  const isAdmin = me?.role === "admin";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/auth/me");
        setMe(data);
        setPhone(data.phone || "");
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load your profile");
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    // Sync from the server exactly ONCE, on first load — the 15s poll
    // underneath sysMode must never silently discard an unsaved edit the
    // VA is mid-way through making.
    if (sysMode.loaded && !categoriesInitRef.current) {
      categoriesInitRef.current = true;
      setCategories(sysMode.policies?.away_escalation?.auto_approve_categories || []);
    }
  }, [sysMode.loaded, sysMode.policies]);

  const toggleCategory = (key) => {
    setCategories((prev) => prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]);
  };

  const saveCategories = async () => {
    setCategoriesSaving(true);
    try {
      await apiClient.put("/system-mode/away-escalation-categories", { categories });
      await sysMode.refresh();
      toast.success("Desk-wide escalation categories saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save categories");
    } finally { setCategoriesSaving(false); }
  };

  const save = async () => {
    const normalizedPhone = autoPrefixUS(phone);
    if (normalizedPhone && !isValidE164(normalizedPhone)) {
      toast.error("That doesn't look like a valid US phone number");
      return;
    }
    setSaving(true);
    try {
      const { data } = await apiClient.patch("/auth/me", { phone: normalizedPhone });
      setMe(data);
      setPhone(data.phone || "");
      toast.success("Profile saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't save your profile");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500" data-testid="my-profile-loading">Loading…</div>;

  return (
    <div className="max-w-2xl p-6 space-y-8" data-testid="my-profile-panel">
      <div>
        <h2 className="text-lg font-bold text-slate-900">My Profile</h2>
        <p className="text-sm text-slate-500 mt-1">{me?.name} · {me?.email} · {me?.role}</p>
      </div>

      <section>
        <label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-2">
          <PhoneCall size={14} /> Call-back phone number
        </label>
        <p className="text-xs text-slate-500 mt-1 mb-2">Used for the Floor's call/SOS features and desk escalation calls.</p>
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onBlur={() => setPhone((p) => autoPrefixUS(p))}
          placeholder="(940) 000-0000"
          data-testid="my-profile-phone-input"
          className="w-full max-w-xs text-sm rounded-md border border-slate-200 px-3 py-2 focus:outline-none focus:border-brandblue"
        />
      </section>

      <section className="rounded-lg border border-slate-200 p-4" data-testid="my-profile-system-mode-section">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-sm font-bold text-slate-900">
            <MoonStars size={16} /> System Mode
          </span>
          <span
            data-testid="my-profile-system-mode-value"
            className={`text-xs font-mono font-bold tracking-widest px-2.5 py-1 rounded-full ${
              sysMode.mode === "away" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
            }`}
          >
            {sysMode.loaded ? (sysMode.mode === "away" ? "AWAY" : "STANDARD") : "…"}
          </span>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          This is the ONE switch for the whole desk — not a personal setting. It's changed from the mode pill on
          the Floor itself (by any admin/VA/dispatcher), so whoever is looking at the Floor always sees exactly
          what's about to change. Standard Mode: Kaizen accepts eligible offers instantly, a human still assigns
          the driver. Away Mode: follows whatever autonomy level an admin has configured
          (Admin → Kaizen Settings → System Mode &amp; Autonomy).
        </p>
      </section>

      {isAdmin && (
        <section data-testid="my-profile-escalation-categories-section">
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-1">
            Desk-wide auto-approve (admin)
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Only applies to the generic "ring the on-duty desk" escalation while the desk is unstaffed — unrelated
            to Kaizen or trip-risk autonomy, which have their own admin-configured policy. Off by default.
          </p>
          <div className="space-y-2">
            {ESCALATION_CATEGORIES.map((c) => {
              const on = categories.includes(c.key);
              return (
                <label key={c.key} className="flex items-start gap-3 rounded-md border border-slate-200 p-3 cursor-pointer hover:border-brandblue/40"
                       data-testid={`auto-approve-row-${c.key}`}>
                  <input type="checkbox" checked={on} onChange={() => toggleCategory(c.key)}
                         data-testid={`auto-approve-checkbox-${c.key}`} className="mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                      {c.label}
                      {on && <CheckCircle size={14} weight="fill" className="text-brandblue" />}
                    </div>
                    <div className="text-xs text-slate-500">{c.detail}</div>
                  </div>
                </label>
              );
            })}
          </div>
          <button
            onClick={saveCategories}
            disabled={categoriesSaving}
            data-testid="my-profile-save-categories-button"
            className="mt-3 px-4 py-2 rounded-md bg-slate-900 text-white text-xs font-bold hover:bg-slate-700 disabled:opacity-50"
          >
            {categoriesSaving ? "Saving…" : "Save categories"}
          </button>
        </section>
      )}

      <button
        onClick={save}
        disabled={saving}
        data-testid="my-profile-save-button"
        className="px-5 py-2.5 rounded-md bg-brandblue text-white text-sm font-bold hover:bg-brandblue/90 disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}
